import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/oraya-api";
import { Seo, Reveal, Overline, SectionHeader } from "@/components/site/primitives";

const CATEGORIES = ["All", "ORAYA", "AI", "Systems", "Industry", "Technology", "Research"];

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); }
  catch { return ""; }
}

export default function Insights() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/insights", { params: cat !== "All" ? { category: cat } : {} })
      .then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div className="pt-36 pb-32">
      <Seo title="Insights — ORAYA" description="Perspectives on AI systems, intelligence and the industries that power the world." path="/insights" />
      <div className="oraya-container">
        <Reveal>
          <SectionHeader overline="Insights" title="Insights that drive intelligence forward." />
        </Reveal>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="insights-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`insight-cat-${c.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${cat === c ? "bg-invert text-invert-ink" : "glass-medium text-subtle hover:text-ink"}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading && <p className="text-subtle" data-testid="insights-loading">Loading…</p>}
        {!loading && items.length === 0 && <p className="text-subtle" data-testid="insights-empty">No insights in this category yet.</p>}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.map((it, i) => (
            <Reveal key={it.id || it.slug} delay={(i % 3) * 0.08}>
              <Link to={`/insights/${it.slug}`} data-testid={`insight-card-${it.slug}`} className="block glass-medium rounded-2xl overflow-hidden hover-lift hover:border-hairline/15 h-full">
                <div className="relative h-44 bg-panel overflow-hidden">
                  {mediaUrl(it.cover_image) ? (
                    <img src={mediaUrl(it.cover_image)} alt={it.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-subtle/30 display text-5xl">O</div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-subtle">
                    <span className="uppercase tracking-[0.15em]">{it.category}</span>
                    <span>·</span>
                    <span>{fmtDate(it.publish_date)}</span>
                  </div>
                  <h3 className="display text-xl font-medium text-ink mt-3 leading-snug">{it.title}</h3>
                  <p className="text-sm text-subtle mt-2 line-clamp-2">{it.description}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
